import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase/config';
import shield from '../assets/shortley-shield.png';
import theme, { getTheme } from '../theme';
import { useDarkMode } from '../context/DarkModeContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const Login = () => {
  useEffect(() => { document.title = 'Sign In — Learnable'; return () => { document.title = 'Learnable'; }; }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const t = getTheme(isDark);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setResetSent(false); setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      const code = err.code;
      if (code === 'auth/wrong-password' || code === 'auth/user-not-found' || code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a moment and try again.');
      } else if (code === 'auth/network-request-failed') {
        setError('Network error. Check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleReset = async () => {
    setError(''); setResetSent(false);
    if (!email) { setError('Enter your email address first.'); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch { setError('Could not send reset email. Check your email address.'); }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: t.bg, fontFamily: theme.fontBody, display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <nav style={{ padding: '0 24px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
          <img src={shield} alt="" style={{ width: '30px', height: '30px', objectFit: 'contain' }} />
          <span style={{ fontSize: '16px', fontWeight: 700, color: t.text, fontFamily: "'DM Serif Display',Georgia,serif" }}>Learnable</span>
        </Link>
        <Link to="/signup" style={{ fontSize: '13px', color: t.textSec, textDecoration: 'none' }}>
          New here? <span style={{ color: t.gold, fontWeight: 600 }}>Sign up →</span>
        </Link>
      </nav>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 16px', position: 'relative' }}>
        {/* Ambient glow */}
        <div style={{ position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)', width: '400px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,168,67,0.06), transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ width: '100%', maxWidth: '420px', position: 'relative' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(212,168,67,0.15), rgba(212,168,67,0.05))', border: `1px solid ${t.goldBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }} aria-hidden="true"></div>
            <h1 style={{ fontSize: '28px', fontWeight: 700, fontFamily: "'DM Serif Display', Georgia, serif", color: t.text, marginBottom: '6px', letterSpacing: '-0.03em' }}>Welcome back</h1>
            <p style={{ fontSize: '14px', color: t.textSec }}>Sign in to continue studying</p>
          </div>

          {/* Form card */}
          <div style={{ backgroundColor: t.surface, border: `1px solid ${t.border}`, borderRadius: '20px', padding: '28px', boxShadow: t.shadow3 }}>
            {error && (
              <div role="alert" style={{ padding: '11px 14px', backgroundColor: t.redGlow, border: `1px solid rgba(224,82,82,0.2)`, borderRadius: '10px', fontSize: '13px', color: t.red, marginBottom: '18px' }}>
                 {error}
              </div>
            )}
            {resetSent && (
              <div role="status" style={{ padding: '11px 14px', backgroundColor: t.greenGlow, border: `1px solid rgba(61,186,110,0.2)`, borderRadius: '10px', fontSize: '13px', color: t.green, marginBottom: '18px' }}>
                ✓ Password reset email sent! Check your inbox.
              </div>
            )}

            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required autoFocus />
              <div>
                <Input id="login-password" label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters" required />
                <div style={{ textAlign: 'right', marginTop: '6px' }}>
                  <button type="button" onClick={handleReset} style={{ fontSize: '11px', color: t.gold, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Forgot password?</button>
                </div>
              </div>
              <Button type="submit" variant="primary" size="lg" disabled={loading} style={{ width: '100%', marginTop: '4px' }}>
                {loading ? 'Signing in...' : 'Sign In →'}
              </Button>
            </form>
          </div>

          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p style={{ fontSize: '13px', color: t.textSec }}>
              New to Learnable?{' '}
              <Link to="/signup" style={{ color: t.gold, fontWeight: 600, textDecoration: 'none' }}>Create a free account →</Link>
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginTop: '28px' }}>
            {[['', 'AI Tutor'], ['', 'Gap Mapper'], ['', 'Study Planner']].map(([icon, label]) => (
              <div key={label} style={{ textAlign: 'center', padding: '12px 8px', backgroundColor: t.surface2, border: `1px solid ${t.border}`, borderRadius: '12px' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }} aria-hidden="true">{icon}</div>
                <div style={{ fontSize: '10px', color: t.textTert, fontWeight: 600 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
